// BillingStatusCard displays the current subscription billing status.
// Used by SubscriptionPage to show tier, status, dates, and a manage billing button.
// When billing is null (admin-provisioned tenant), renders a simplified tier card.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { platformApi, type SubscriptionInfo, type BillingState } from '@/lib/api/platform'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ExternalLink } from 'lucide-react'

interface BillingStatusCardProps {
  info: SubscriptionInfo
}

// Format a date string to a readable format: "5 April 2026"
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function statusBadgeVariant(
  status: BillingState['status'],
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'trialing':
      return 'secondary'
    case 'active':
      return 'default'
    case 'past_due':
    case 'unpaid':
      return 'destructive'
    case 'cancelled':
      return 'outline'
    default:
      return 'outline'
  }
}

function DateLine({ billing, t }: { billing: BillingState; t: ReturnType<typeof useTranslation>['t'] }) {
  const { status, trial_end, current_period_end, cancel_at } = billing

  if (status === 'trialing' && trial_end) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('subscription.billing.trialEnds', { date: formatDate(trial_end) })}
      </p>
    )
  }

  if (status === 'active' && cancel_at) {
    return (
      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
        {t('subscription.billing.cancelsOn', { date: formatDate(cancel_at) })}
      </p>
    )
  }

  if (status === 'active') {
    return (
      <p className="text-sm text-muted-foreground">
        {t('subscription.billing.renews', { date: formatDate(current_period_end) })}
      </p>
    )
  }

  if (status === 'past_due') {
    const band = billing.grace_period_band ?? 0
    const key =
      band === 0
        ? 'subscription.billing.gracePeriodBand0'
        : band === 1
          ? 'subscription.billing.gracePeriodBand1'
          : 'subscription.billing.gracePeriodBand2'
    return <p className="text-sm font-medium text-destructive">{t(key)}</p>
  }

  return null
}

export function BillingStatusCard({ info }: BillingStatusCardProps) {
  const { t } = useTranslation('platform')
  const api = useApi()
  const [portalError, setPortalError] = useState<string | null>(null)

  const portalMutation = useMutation({
    mutationFn: () => platformApi(api).createCustomerPortalSession(),
    onSuccess: (data) => {
      window.location.href = data.portal_url
    },
    onError: () => {
      setPortalError(t('subscription.billing.manageBillingError'))
    },
  })

  const handleManageBilling = () => {
    setPortalError(null)
    portalMutation.mutate()
  }

  const { billing, tier_display_name } = info

  // Simplified card for admin-provisioned tenants (no billing row)
  if (!billing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('subscription.tier')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold">{tier_display_name}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('subscription.billing.directSubscription')}
          </p>
        </CardContent>
      </Card>
    )
  }

  const statusLabel =
    t(`subscription.billing.statusLabels.${billing.status}`) || billing.status
  const showManageBilling = billing.status !== 'cancelled'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('subscription.tier')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tier name + status badges */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-lg font-semibold">{tier_display_name}</span>
          <Badge variant={statusBadgeVariant(billing.status)}>{statusLabel}</Badge>
        </div>

        {/* Date line */}
        <DateLine billing={billing} t={t} />

        {/* Billing interval */}
        <p className="text-sm text-muted-foreground">
          {billing.billing_interval === 'month'
            ? t('subscription.billing.monthlyBilling')
            : t('subscription.billing.annualBilling')}
        </p>

        {/* GenAI API */}
        <p className={`text-sm ${billing.api_access ? 'text-foreground' : 'text-muted-foreground'}`}>
          {billing.api_access
            ? t('subscription.billing.genaiIncluded')
            : t('subscription.billing.genaiNotIncluded')}
        </p>

        {/* Manage billing button */}
        {showManageBilling && (
          <div className="pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleManageBilling}
              disabled={portalMutation.isPending}
            >
              <ExternalLink className="mr-2 h-3.5 w-3.5" />
              {portalMutation.isPending
                ? t('subscription.billing.manageBillingOpening')
                : t('subscription.billing.manageBilling')}
            </Button>
            {portalError && (
              <p className="mt-2 text-sm text-destructive">{portalError}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
